import { useState } from "react";
// material
import { Alert, Button, Stack, Typography } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import axios from "@utils/axios";
import IdentityFields, { EMPTY_IDENTITY, IdentityValues } from "./IdentityFields";
import { failureMessage } from "./failureMessage";

/**
 * 로그인할 때의 본인인증. **문자(SMS)** 방식이다.
 *
 * 링크를 눌렀는데 인증이 남았을 때 나온다(게이트가 «로그인 매번» 이거나 «관리자
 * 로그인» 일 때). 로그인 화면에서 미리 인증하는 흐름과 입력 항목은 같으므로
 * `IdentityFields` 를 공유한다 — 각자 그리면 한쪽만 고쳤을 때 조용히 갈라진다.
 *
 * ★ 예전에는 이름·생년월일·번호 셋만 받고 성별·내외국인·통신사에 «모름(`x`)» 을
 * 보냈다. **KCB 가 그것을 받아 주지 않는다** — 운영에서 «휴대폰 통신사 정보를
 * 확인해주세요» 로 막혔다. 주민번호 뒤 한 글자와 통신사를 받아야 한다.
 *
 * 인증이 끝나면 `onDone` 을 부른다. 세션을 여는 것은 부모(`MagicLink`)의 일이다 —
 * 이 컴포넌트는 «이 사람이 본인인가» 까지만 책임진다.
 */
export default function IdentityForm({ token, onDone, onlyOnce = false }: {
  /** 메일로 받은 로그인 링크의 토큰 */
  token: string;
  onDone: () => void;
  /**
   * **이번 한 번뿐인가.**
   *
   * 지문이 아직 없어서 받는 인증이면 참이다. 관리자가 «매번» 으로 켜 둔 경우와
   * 문장이 달라야 한다 — 예상 못 한 단계를 만난 사람에게 「이번만 하면 된다」는
   * 것을 알려 주지 않으면 「앞으로 매번 이러나」로 읽는다.
   */
  onlyOnce?: boolean;
}) {
  const [values, setValues] = useState<IdentityValues>(EMPTY_IDENTITY);
  /** 인증번호를 보낸 뒤에만 참이다. 있으면 «번호 입력» 단계다. */
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState("");
  /**
   * 더 눌러도 소용없는 거절을 받았다 — 계정 이름과 인증한 이름이 달라 403(«고객센터로 문의»).
   * 그 뒤에도 «인증 완료» · «인증번호 다시 받기» 가 눌려서 누를 때마다 문자가 나갔다(f1-5).
   * 잠그고 «로그인 화면으로» 만 남긴다(부모 `MagicLink` 가 그린다).
   */
  const [stuck, setStuck] = useState(false);

  const send = async (agreedTerms: string[]) => {
    setBusy(true);
    setFailed("");
    try {
      await axios({
        method: "post",
        url: "auth/identity/request",
        data: {
          token,
          name: values.name.trim(),
          regFront: values.regFront,
          regRear: values.regRear,
          telComCd: values.telComCd,
          telNo: values.telNo.replace(/\D/g, ""),
          // 서버가 다시 본다. 이유는 `IdentityFields` 의 `onRequestOtp` 주석에.
          agreedTerms,
        },
      });
      setSent(true);
    } catch (error: any) {
      // 문자열일 때만 그대로 쓴다. 422 의 배열을 그리면 React 가 터진다(2026-09-23 B7).
      setFailed(failureMessage(error, {
        fallback: "인증번호를 보내지 못했습니다. 잠시 후 다시 시도해주세요.",
      }));
      if (Number(error?.httpStatus) === 403) setStuck(true);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    setFailed("");
    try {
      await axios({
        method: "post",
        url: "auth/identity/confirm",
        data: { token, telNo: values.telNo.replace(/\D/g, ""), otpNo: values.otpNo },
      });
      onDone();
    } catch (error: any) {
      setFailed(failureMessage(error, { fallback: "인증에 실패했습니다." }));
      if (Number(error?.httpStatus) === 403) setStuck(true);
      setBusy(false);
    }
  };

  return (
    <Stack spacing={3}>
      {/* 「왜 갑자기?」에 답한다. 매번 받는 것과 처음 한 번은 사용자에게 전혀
          다른 일이라, 같은 문장으로 뭉개면 안 된다. */}
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {onlyOnce
          ? "처음 한 번만 본인인증이 필요합니다. 확인이 끝나면 다음 로그인부터는 묻지 않습니다."
          : "로그인하려면 본인인증이 필요합니다. 휴대폰으로 인증번호를 보내드립니다."}
      </Typography>
      {failed && <Alert severity="error">{failed}</Alert>}

      <IdentityFields
        values={values}
        onChange={setValues}
        sent={sent}
        sending={busy && !sent}
        onRequestOtp={send}
        disabled={stuck || (busy && sent)}
      />

      {sent && !stuck && (
        <>
          <LoadingButton
            fullWidth size="large" variant="contained" loading={busy}
            disabled={values.otpNo.length < 4}
            onClick={confirm}
          >
            인증 완료
          </LoadingButton>
          {/* 번호를 잘못 넣었을 때 돌아갈 길. 다시 보내면 서버가 새 거래번호를
              만들어 이 링크에 다시 적는다. */}
          <Button
            fullWidth
            onClick={() => {
              setSent(false);
              setValues({ ...values, otpNo: "" });
              setFailed("");
            }}
          >
            정보를 다시 입력하기
          </Button>
        </>
      )}

    </Stack>
  );
}
